/**
 * ReportBuilderEditorProvider
 *
 * Owns the docx editor ref + all per-session state (active case +
 * template, module content, drafts, choices) for the Report Builder.
 * Mounted in <Layout/>.
 *
 * The editor engine is docx-editor.dev (@eigenpal/docx-editor-react).
 * Unlike the previous Tiptap instance, a <DocxEditor> cannot be shared
 * live across mounts — so the provider holds the editor *ref* + the
 * loaded document buffer, and col-3 (<ReportSectionEditor/>) lazily
 * renders the (heavy) <DocxEditor> from them. Cross-session persistence
 * is the uploaded WIP .docx blob (draft_docx_download_url), not a live
 * shared view.
 *
 * Lifecycle:
 *   1. Provider mounts at app start (cheap — no docx engine here).
 *   2. <ReportBuilderPage/> calls `activate(caseNumber, templateId)`;
 *      provider resolves template + draft, then loads the WIP .docx
 *      buffer (preferring the saved draft over the bare template).
 *   3. col-3 renders <DocxEditor documentBuffer={buffer} ref={editorRef}/>.
 *   4. Autosave (debounced) persists draftData + uploads the editor's
 *      current .docx; a refresh restores from that blob.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import type { DocxEditorRef } from "@eigenpal/docx-editor-react";
import { useQueryClient } from "@tanstack/react-query";
import { useDispatch } from "react-redux";

import { CLIENT_KEYS } from "@constants/clientKeys";
import { getJurisdictionLabel } from "@constants/jurisdictions";

import {
  useCompleteResponseMutation,
  useDownloadResponseDocx,
  useSaveDraftMutation,
  useUploadDraftDocxMutation,
} from "@hooks/mutations/use-templates";
import { useCaseByCaseNumber } from "@hooks/queries/use-cases";
import { useTemplateOutline } from "@hooks/queries/use-template-outline";
import {
  useCheckDraftStatus,
  useSourceSnippets,
  useTemplateById,
  useTemplateRawDocxBuffer,
} from "@hooks/queries/use-templates";

import { setMessage } from "@redux/notification";

import { toTitleCase } from "@utils/string";

import {
  CODE_OF_CONDUCT_HEADING_TITLE,
  LIST_OF_DOCUMENTS_HEADING_TITLE,
} from "../constants";
import { buildDocSignature, classifyEditChange } from "./autosave";
import type { ReportNote } from "./notes";
import { countNotesBySection, flattenNotes, notesForSection } from "./notes";
import { buildOutlineHints } from "./qta-insert";
import type { QtaDraftStatus } from "./qta-source";
import { deriveQtaSource } from "./qta-source";
import type {
  ModuleChoice,
  ModuleContent,
  ModuleDraft,
  OrderedModule,
  SectionRow,
  SectionStatus,
} from "./utils";
import {
  buildSectionRows,
  getModuleChoices,
  orderedModulesFromTemplate,
  resolveChoiceKey,
  resolveSeedHtml,
  resolveSeedText,
} from "./utils";

const AUTO_SAVE_DEBOUNCE_MS = 1500;

// SectionRow / SectionStatus moved to ./utils so the row-building logic can be
// unit-tested in isolation; re-exported here for existing consumers.
export type { SectionRow, SectionStatus };

type ContextValue = {
  // Activation
  isActive: boolean;
  activeCase: string | null;
  activeTemplate: string | null;
  activate: (caseNumber: string, templateId: string) => void;

  // Editor (docx) + loading
  editorRef: React.RefObject<DocxEditorRef | null>;
  documentBuffer: ArrayBuffer | undefined;
  isBufferLoading: boolean;
  bufferError: unknown;
  onEditorChange: () => void;
  /** True when the editor doc has no text — autosave skips, UI can gate Save. */
  isEditorEmpty: () => boolean;
  isLoading: boolean;
  lastSavedAt: number | null;
  /** Draft response id (for the PDF export endpoint); null until a draft exists. */
  draftId: string | null;

  // Server-side data
  template: ReturnType<typeof useTemplateById>["data"];
  caseData: ReturnType<typeof useCaseByCaseNumber>["data"];
  orderedModules: OrderedModule[];

  // Module state
  moduleContent: Record<string, ModuleContent>;
  choiceByModule: Record<string, string>;
  /** Source data for the Question To Answer module — the raw LOI
   *  questions text (`summaries_from_doc_sorter`) and any saved per-
   *  question answers (`experts_response`). `null` when no QtA module
   *  is present in the active template. Consumed by the editor's QtA
   *  insert helper. */
  qtaSource: {
    moduleName: string;
    rawQuestions: string;
    expertsResponse: string;
  } | null;
  /** Source data for the List-of-Documents-Provided module — the doc-
   *  sorter document schedule (`summaries_from_doc_sorter`) and any saved
   *  expert commentary (`experts_response`). `null` when no LoI module is
   *  present in the active template (or when no document data exists yet).
   *  Consumed by the editor's documents-table insert helper (MDRS-98). */
  documentsSource: {
    moduleName: string;
    summaryText: string;
    expertsResponse: string;
  } | null;
  /** Pre-classified paragraph text from the backend template-outline
   *  endpoint, bucketed by classification. Frontend insertion helpers
   *  read these in priority over local style-based heuristics so the
   *  same per-template knowledge (computed once server-side) drives
   *  heading / signature / closing / chrome detection. `null` while the
   *  outline is still loading, or when the case has no uploaded
   *  template — callers must fall back to local heuristics in those
   *  cases. */
  outlineHints: {
    knownHeadings: Set<string>;
    knownChrome: Set<string>;
    knownClosing: Set<string>;
    knownSignature: Set<string>;
  } | null;
  /** Auto-discovered insertion anchors from the backend outline. The
   *  CoC anchor is the first section heading after the case-context
   *  opening (`Thank you for your referral…`); the QtA anchor is the
   *  last section heading before the signature / closing-phrase block.
   *  Frontend uses these in priority over the hard-coded fallback lists
   *  in ReportSectionEditor — that's how a brand-new template with
   *  novel heading names ("MY BACKGROUND AND EXPERTISE", etc.) gets
   *  the right insertion point without a code change. */
  outlineAnchors: {
    cocAnchor: string | null;
    qtaAnchor: string | null;
  };

  // Section navigation
  activeSection: string | null;
  setActiveSection: (key: string) => void;
  handleSelectSection: (key: string) => void;
  /**
   * "Show All Notes" — when on, the Notes panel lists every section's notes
   * and the active section is deselected (the user must reselect a section to
   * leave All-Notes). Lives here, not in the panel, because it drives
   * `activeSection`. See `setShowAll`.
   */
  showAll: boolean;
  setShowAll: (on: boolean) => void;
  sectionRows: SectionRow[];
  activeRow: SectionRow | null;
  activeModule: OrderedModule["module"] | null;
  activeChoices: ModuleChoice[] | null;
  /** Notes for the active section ("This section" tab). */
  activeNotes: ReportNote[];
  /** All notes across sections ("All sections" tab). */
  allNotes: ReportNote[];

  // Handlers
  handleChoiceChange: (moduleName: string, newKey: string) => void;
  handleSave: () => Promise<void>;
  handleDownload: () => Promise<void>;

  // Status
  isSaving: boolean;
  isDownloading: boolean;
};

const ReportBuilderEditorContext = createContext<ContextValue | null>(null);

export const useReportBuilderEditor = (): ContextValue => {
  const ctx = useContext(ReportBuilderEditorContext);
  if (!ctx) {
    throw new Error(
      "useReportBuilderEditor must be used within <ReportBuilderEditorProvider/>"
    );
  }
  return ctx;
};

export const ReportBuilderEditorProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  // ── Activation ──────────────────────────────────────────────────────
  // `activate(case, template)` is called by the Report Builder page on
  // mount. We track the active pair so the editor survives navigation to
  // sibling tabs (Assessment Prep / Scribe) and the floating drawer can
  // render the same content there.
  const [activeCase, setActiveCase] = useState<string | null>(null);
  const [activeTemplate, setActiveTemplate] = useState<string | null>(null);

  const activate = useCallback((caseNumber: string, templateId: string) => {
    setActiveCase(caseNumber);
    setActiveTemplate(templateId);
  }, []);

  // ── Server data ─────────────────────────────────────────────────────
  const { data: template, isLoading: isTemplateLoading } = useTemplateById(
    activeTemplate ?? "",
    {
      enabled: !!activeTemplate,
    }
  );
  const { data: draftStatus, isLoading: isDraftLoading } = useCheckDraftStatus({
    templateId: activeTemplate ?? "",
    caseNumber: activeCase ?? "",
    enabled: !!activeTemplate && !!activeCase,
  });
  const { data: caseData, isLoading: isCaseLoading } = useCaseByCaseNumber({
    case_number: activeCase ?? "",
    enabled: !!activeCase,
  });

  // Editor document buffer.
  //
  // Resolution order, so the editor always shows the user's most recent
  // work:
  //   1. Saved WIP draft (`draft_docx_download_url`) — the user has
  //      already edited this report; restore from there.
  //   2. The case's uploaded report template
  //      (`caseData.report_template_url`) — the .docx the user uploaded
  //      at case creation. We render it as-is, no module substitution,
  //      until the heading-matching pipeline ships (interim — see
  //      [[use-case-uploaded-template]]).
  //   3. The system template's bare .docx — last-resort fallback for
  //      cases without a report_template_path (older cases or seed data).
  //
  // The hook's `draftDocxDownloadUrl` override branch fetches an
  // arbitrary URL directly, which is exactly the shape we need for the
  // case-upload path too. We chain (1) → (2) here; (3) is what the hook
  // does natively when no override is passed.
  const editorBufferOverrideUrl =
    draftStatus?.draft_docx_download_url ?? caseData?.report_template_url ?? null;
  const {
    data: documentBuffer,
    isLoading: isBufferLoading,
    error: bufferError,
  } = useTemplateRawDocxBuffer(activeTemplate ?? "", {
    enabled: !!activeTemplate && !isDraftLoading && !isCaseLoading,
    draftDocxDownloadUrl: editorBufferOverrideUrl,
  });

  const { mutateAsync: saveDraft, isPending: isSaving } = useSaveDraftMutation({
    onError: (error) => {
      console.error("Failed to save draft:", error);
      dispatch(setMessage({ type: "error", content: "Failed to save draft." }));
    },
  });
  const { mutateAsync: uploadDraftDocx } = useUploadDraftDocxMutation();
  const downloadDocx = useDownloadResponseDocx();
  // Pin the stable bits of the mutation result so the `handleDownload`
  // useCallback doesn't churn each render — same pattern as the PDF
  // download wiring in ReportBuilder/index.tsx.
  const downloadDocxMutateAsync = downloadDocx.mutateAsync;
  const { mutateAsync: completeResponse } = useCompleteResponseMutation({
    onError: (error) => console.error("Failed to complete response:", error),
  });

  // ── Editor ref + activity tracking ──────────────────────────────────
  const editorRef = useRef<DocxEditorRef | null>(null);
  // Flags whether the user has made ≥1 edit, so the initial draft seed
  // doesn't trigger an autosave round-trip.
  const hasUserEditedRef = useRef(false);
  // Bumped on every editor change so the debounced autosave effect (which
  // can't key on moduleContent — the docx editor doesn't mirror into it)
  // actually re-runs and reschedules.
  const [editTick, setEditTick] = useState(0);
  // Signature of the document content as of the last save. The docx editor
  // fires onChange for NON-content mutations too — `setZoom` (called on every
  // ResizeObserver tick / column toggle), selection, layout/pagination — and
  // each of those used to mark the doc dirty and re-save, which kept resetting
  // lastSavedAt so the label was stuck on "just now". Compare the content
  // signature so only real edits count as a change.
  const lastSavedSigRef = useRef<string | null>(null);
  const docSignature = useCallback(
    (): string =>
      buildDocSignature(editorRef.current?.getEditorRef?.()?.getState?.()?.doc),
    []
  );
  const onEditorChange = useCallback(() => {
    // classifyEditChange distinguishes the post-load baseline + non-content
    // mutations (zoom/selection/layout) from real edits — see ./autosave.
    const outcome = classifyEditChange(docSignature(), lastSavedSigRef.current);
    if (outcome.kind === "baseline") {
      lastSavedSigRef.current = outcome.signature;
      return;
    }
    if (outcome.kind === "ignore") return;
    hasUserEditedRef.current = true;
    setEditTick((t) => t + 1);
  }, [docSignature]);
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  // Whether the docx editor's document has no text. Read straight off the
  // ProseMirror view (the ref exposes no isEmpty()). Used to skip autosave
  // when the doc is blank — so an empty editor never overwrites a real saved
  // draft — and surfaced to the UI to gate Save/Download.
  const isEditorEmpty = useCallback(() => {
    const state = editorRef.current?.getEditorRef?.()?.getState?.();
    if (!state) return true;
    return state.doc.textContent.trim().length === 0;
  }, []);

  // ── Notes (snippets) — /source-snippets, the col-2 data source. ─────
  const { data: sourceSnippets } = useSourceSnippets({
    templateId: activeTemplate ?? "",
    caseNumber: activeCase ?? "",
    enabled: !!activeTemplate && !!activeCase,
  });
  const allNotes = useMemo<ReportNote[]>(
    () => flattenNotes(sourceSnippets ?? []),
    [sourceSnippets]
  );
  const noteCountBySection = useMemo(
    () => countNotesBySection(allNotes),
    [allNotes]
  );

  // ── Module state ────────────────────────────────────────────────────
  const [moduleContent, setModuleContent] = useState<Record<string, ModuleContent>>(
    {}
  );
  const moduleContentRef = useRef(moduleContent);
  useEffect(() => {
    moduleContentRef.current = moduleContent;
  }, [moduleContent]);

  const [choiceByModule, setChoiceByModule] = useState<Record<string, string>>({});
  const choiceByModuleRef = useRef(choiceByModule);
  useEffect(() => {
    choiceByModuleRef.current = choiceByModule;
  }, [choiceByModule]);

  const acceptedFromServerRef = useRef<Set<string>>(new Set());

  const orderedModules = useMemo(
    () => orderedModulesFromTemplate(template),
    [template]
  );

  // ── Initial seed ────────────────────────────────────────────────────
  // Editor content comes from the .docx buffer (template or saved WIP);
  // here we only seed per-module text/html + choices from the draft so
  // the section list, choices, and draft payload stay populated. No
  // editor.setContent — the <DocxEditor> mounts from `documentBuffer`.
  const hasInitializedRef = useRef(false);
  useEffect(() => {
    if (hasInitializedRef.current) return;
    if (!activeTemplate || !activeCase) return;
    if (isTemplateLoading || isDraftLoading) return;
    if (!template) return;

    hasInitializedRef.current = true;
    const draftData = (draftStatus?.draft_data ?? {}) as Record<
      string,
      ModuleDraft & { is_accepted?: boolean }
    >;

    const initial: Record<string, ModuleContent> = {};
    const initialChoices: Record<string, string> = {};
    const accepted = new Set<string>();
    for (const { module } of orderedModules) {
      const draft = draftData[module.name];
      const text = resolveSeedText(module, draft);
      const html = resolveSeedHtml(module, draft);
      initial[module.name] = {
        name: module.name,
        label: toTitleCase(module.name),
        text,
        html,
      };
      const choices = getModuleChoices(module);
      if (choices) {
        initialChoices[module.name] = resolveChoiceKey(choices, draft?.value);
      }
      if (draft?.is_accepted) accepted.add(module.name);
    }
    acceptedFromServerRef.current = accepted;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setModuleContent(initial);
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setChoiceByModule(initialChoices);
  }, [
    isTemplateLoading,
    isDraftLoading,
    template,
    activeTemplate,
    activeCase,
    draftStatus,
    orderedModules,
  ]);

  // ── Derived section rows. ───────────────────────────────────────────
  // Filtering + numbering live in buildSectionRows (./utils, unit-tested):
  // auto-inserted preamble/LOI sections are always hidden; every remaining
  // (authorable) section is shown regardless of mapped-note count. Note count
  // only drives the col-1 badge, not visibility.
  const sectionRows: SectionRow[] = useMemo(() => {
    return buildSectionRows({
      orderedModules,
      moduleContent,
      acceptedModuleNames: acceptedFromServerRef.current,
      noteCountBySection,
    });
  }, [moduleContent, orderedModules, noteCountBySection]);

  // ── Active section + Show All Notes ─────────────────────────────────
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [showAll, setShowAllState] = useState(false);

  // Keep the active section pointing at a visible row — but NOT while All-Notes
  // is on, where a null activeSection is the intended state (MDRS-8).
  // Auto-picks on first load, and re-picks if the current section drops out of
  // the visible list (e.g. its last mapped note was removed, so it no longer
  // qualifies under MDRS-63).
  useEffect(() => {
    if (showAll) return;
    if (sectionRows.length === 0) return;
    if (activeSection && sectionRows.some((s) => s.key === activeSection)) {
      return;
    }
    const firstDraft = sectionRows.find((s) => s.status === "draft");
    setActiveSection(firstDraft?.key ?? sectionRows[0].key);
  }, [showAll, activeSection, sectionRows]);

  // Turning All-Notes on deselects the active section; the user reselects a
  // section (which turns All-Notes back off) to return to the section view.
  const setShowAll = useCallback((on: boolean) => {
    setShowAllState(on);
    if (on) setActiveSection(null);
  }, []);

  // ── Reset on (case, template) change ────────────────────────────────
  // Switching to a different report tears down our cached module state so
  // the seed effect can re-init from the new template's modules + draft.
  const lastActivationRef = useRef<string | null>(null);
  useEffect(() => {
    const key =
      activeCase && activeTemplate ? `${activeCase}|${activeTemplate}` : null;
    if (lastActivationRef.current === key) return;
    const isFirstActivation = lastActivationRef.current === null;
    lastActivationRef.current = key;
    if (isFirstActivation) return;
    hasInitializedRef.current = false;
    setModuleContent({});
    setChoiceByModule({});
    setActiveSection(null);
    setShowAllState(false);
    acceptedFromServerRef.current = new Set();
  }, [activeCase, activeTemplate]);

  const activeRow = activeSection
    ? (sectionRows.find((s) => s.key === activeSection) ?? null)
    : null;

  const activeModule = useMemo(() => {
    if (!activeSection) return null;
    return (
      orderedModules.find(({ module }) => module.name === activeSection)?.module ??
      null
    );
  }, [activeSection, orderedModules]);

  const activeChoices: ModuleChoice[] | null = useMemo(
    () => (activeModule ? getModuleChoices(activeModule) : null),
    [activeModule]
  );

  const handleChoiceChange = useCallback(
    (moduleName: string, newKey: string) => {
      setChoiceByModule((prev) => ({ ...prev, [moduleName]: newKey }));
      const m = orderedModules.find((om) => om.module.name === moduleName);
      if (!m) return;
      const choices = getModuleChoices(m.module);
      if (!choices) return;
      const choice = choices.find((c) => c.key === newKey);
      if (!choice) return;
      const description = choice.description || choice.display_name;
      // Mirror the chosen boilerplate into module state (drives the draft
      // payload). Inserting it into the docx editor doc happens in col-3
      // (choice → editor slot insert), tracked in the migration plan §3.1.4.
      hasUserEditedRef.current = true;
      setModuleContent((prev) => {
        const existing = prev[moduleName];
        const label = toTitleCase(moduleName);
        return {
          ...prev,
          [moduleName]: {
            name: moduleName,
            label,
            text: description,
            html: existing?.html ?? "",
          },
        };
      });
    },
    [orderedModules]
  );

  // Section selection no longer scrolls the (HTML) editor; the docx editor
  // is a paginated surface. Selecting a section just drives col-1/col-2.
  const handleSelectSection = (name: string) => {
    // Selecting a section always returns to the section view.
    setShowAllState(false);
    setActiveSection(name);
  };

  // ── Auto-pick Code of Conduct choice from case jurisdiction ──────────
  // The case carries `jurisdiction` (set at case creation). The CoC module
  // is hidden from the sections list, so the user can't pick a jurisdiction
  // here — instead we resolve the matching CoC choice as soon as both the
  // jurisdiction and the module are available. The chosen text then flows
  // through moduleContent into the editor via applyModuleContent.
  //
  // We only fire if the saved draft has no `value` recorded for the CoC
  // module — the seed populates choiceByModule with a fallback (first
  // choice) when no draft exists, so we can't trust that to gate us;
  // checking the raw draft_data tells us whether a real selection (auto
  // or manual) is already persisted.
  const caseJurisdiction = caseData?.jurisdiction ?? null;
  const autoChoiceFiredFor = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!caseJurisdiction) return;
    if (!hasInitializedRef.current) return;
    const draftData = (draftStatus?.draft_data ?? {}) as Record<string, ModuleDraft>;
    const slug = caseJurisdiction.toLowerCase();
    const label = getJurisdictionLabel(slug).toLowerCase();
    for (const { module } of orderedModules) {
      if (!CODE_OF_CONDUCT_HEADING_TITLE.includes(module.name)) continue;
      if (autoChoiceFiredFor.current.has(module.name)) continue;
      if (draftData[module.name]?.value?.trim()) {
        // Persisted choice exists — respect it. Mark fired so we don't
        // re-check on every render.
        autoChoiceFiredFor.current.add(module.name);
        continue;
      }
      const choices = getModuleChoices(module);
      if (!choices) continue;
      const matched = choices.find((c) => {
        const k = c.key.toLowerCase();
        const dn = c.display_name.toLowerCase();
        return k === slug || (!!label && (k === label || dn === label));
      });
      if (!matched) continue;
      autoChoiceFiredFor.current.add(module.name);
      handleChoiceChange(module.name, matched.key);
    }
  }, [caseJurisdiction, orderedModules, draftStatus, handleChoiceChange]);

  // Reset the auto-choice gate when switching cases.
  useEffect(() => {
    autoChoiceFiredFor.current = new Set();
  }, [activeCase, activeTemplate]);

  // ── Active section notes (mapped snippets for the active section). ──
  const activeNotes = useMemo<ReportNote[]>(
    () => notesForSection(allNotes, activeSection),
    [allNotes, activeSection]
  );

  // ── Save / generate / preview ───────────────────────────────────────
  const buildDraftPayload = useCallback(() => {
    const payload: Record<string, unknown> = {};
    for (const { module } of orderedModules) {
      const m = moduleContentRef.current[module.name];
      if (!m) continue;
      const choiceValue = choiceByModuleRef.current[module.name] ?? null;
      payload[module.name] = {
        value: choiceValue,
        summaries_from_doc_sorter: null,
        summaries_from_assessment_scribe: null,
        experts_response: m.text || null,
        experts_response_html: m.html || null,
        generated_collective_response: null,
        original_experts_response: null,
        is_accepted: !!m.text.trim(),
      };
    }
    return payload as Record<
      string,
      Parameters<typeof saveDraft>[0]["draftData"][string]
    >;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderedModules]);

  // Quiet (no-toast) save used by both autosave and handleSave. Persists
  // the draft payload, then (when `uploadDocx` is true) uploads the editor's
  // current .docx as the WIP blob so a refresh restores the user's direct-
  // editor edits. The .docx upload failing must not poison the draft save.
  //
  // `uploadDocx` is false for the on-mount seed save. The seed save exists
  // only to create the DRAFT response row that /source-snippets keys off —
  // uploading the editor buffer at that moment would replace the case's
  // uploaded report_template_url with the editor's round-tripped copy, and
  // any header/footer/media that the editor's serializer can't perfectly
  // round-trip would be lost on the next refresh (the saved blob takes
  // priority over the case template). Real user edits still trigger
  // autosave with uploadDocx=true.
  const runSave = useCallback(
    async ({
      uploadDocx = true,
    }: {
      uploadDocx?: boolean;
    } = {}) => {
      if (!activeTemplate || !activeCase) return;
      const payload = buildDraftPayload();
      await saveDraft({
        templateId: activeTemplate,
        caseNumber: activeCase,
        draftData: payload,
      });
      for (const name of Object.keys(payload)) {
        if (moduleContentRef.current[name]?.text?.trim()) {
          acceptedFromServerRef.current.add(name);
        } else {
          acceptedFromServerRef.current.delete(name);
        }
      }
      if (uploadDocx) {
        // Persist the editor's .docx blob on every save. `save()` preserves
        // embedded media: repack copies existing `word/media/*` verbatim and
        // writes newly-pasted `data:` images into `word/media/` with their
        // relationships (processNewImages). MDRS-218: the previous media-skip
        // workaround here dated from an older build whose save() dropped image
        // binaries — it's now obsolete AND was itself the data-loss bug, since
        // autosave showed "Autosaved" while silently skipping the upload for
        // any doc containing an image. Uploading unconditionally is what keeps
        // body edits + images from being lost on refresh.
        try {
          const docx = await editorRef.current?.save();
          if (docx) {
            await uploadDraftDocx({
              templateId: activeTemplate,
              caseNumber: activeCase,
              docx,
            });
          }
        } catch (err) {
          console.warn("[autosave] draft .docx upload failed:", err);
        }
      }
      setLastSavedAt(Date.now());
      // Clear the dirty flag now that the edits are persisted. Without this
      // the autosave effect (which depends on `isSaving`) re-arms its timer
      // on every isSaving false→true→false toggle and re-saves forever —
      // the "Saving…"/"Autosaved just now" loop.
      hasUserEditedRef.current = false;
      // Snapshot the content so subsequent non-content onChange events (zoom,
      // selection) don't re-mark the doc dirty. Skip if the editor view isn't
      // mounted yet (seed save before the editor parses the buffer) — leave
      // the baseline null so the first real onChange establishes it.
      const sig = docSignature();
      if (sig) lastSavedSigRef.current = sig;
      // The first save creates the DRAFT response row that /source-snippets
      // needs; refetch so the Notes panel populates.
      void queryClient.invalidateQueries({
        queryKey: [
          CLIENT_KEYS.REPORT_GENERATOR,
          CLIENT_KEYS.TEMPLATES,
          activeTemplate,
          CLIENT_KEYS.CASES,
          activeCase,
          "source-snippets",
        ],
      });
      // Also refetch draft-status so `draft_id` populates after the very
      // first save — the PDF download button keys off it (canDownloadPdf)
      // and is disabled until a draft exists.
      void queryClient.invalidateQueries({
        queryKey: [
          CLIENT_KEYS.REPORT_GENERATOR,
          CLIENT_KEYS.TEMPLATES,
          activeTemplate,
          CLIENT_KEYS.CASES,
          CLIENT_KEYS.DRAFT_STATUS,
          activeCase,
        ],
      });
    },
    [
      buildDraftPayload,
      activeCase,
      saveDraft,
      uploadDraftDocx,
      activeTemplate,
      queryClient,
      docSignature,
    ]
  );

  const handleSave = useCallback(async () => {
    // Explicit Save — force the .docx upload even when the doc has
    // media. The user has chosen to commit this exact state; without
    // the upload, `draft_docx_url` stays null and any subsequent
    // Download-PDF returns 404. The trade-off is that a refresh after
    // an explicit Save may show empty `<img>` boxes for media that
    // the editor's `save()` couldn't fully round-trip, but that's a
    // conscious user action rather than the silent autosave path.
    await runSave();
    dispatch(setMessage({ type: "success", content: "Draft saved." }));
  }, [runSave, dispatch]);

  // ── Auto-save (debounced) ───────────────────────────────────────────
  // Fires AUTO_SAVE_DEBOUNCE_MS after the user stops editing. Gated by
  // hasUserEditedRef so the seed doesn't trigger a save, and skipped when the
  // editor is empty so a blank doc never overwrites a real saved draft.
  //
  // The effect is driven only by edit signals (editTick / choiceByModule) —
  // NOT isSaving — to avoid the re-arm loop a stale design had (each save's
  // isSaving toggle re-ran the effect and re-saved). If a save is already
  // in-flight when the timer fires we reschedule rather than drop, so the
  // edit isn't lost; `isSaving` is read through a ref to stay current without
  // being a dependency.
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isSavingRef = useRef(isSaving);
  isSavingRef.current = isSaving;
  useEffect(() => {
    if (!hasUserEditedRef.current) return;
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    const tick = () => {
      // A save is running — try again shortly so this edit still persists.
      if (isSavingRef.current) {
        autoSaveTimerRef.current = setTimeout(tick, AUTO_SAVE_DEBOUNCE_MS);
        return;
      }
      if (isEditorEmpty()) return;
      void runSave();
    };
    autoSaveTimerRef.current = setTimeout(tick, AUTO_SAVE_DEBOUNCE_MS);
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
  }, [editTick, choiceByModule, isEditorEmpty, runSave]);

  // ── Seed-save on mount ──────────────────────────────────────────────
  // /source-snippets returns [] until a DRAFT response row exists; a save
  // creates that row. Fire one quiet save once the seed has run on a
  // fresh case so the Notes panel can populate. Ref-gated to run once.
  // Skip the .docx upload here — the user hasn't edited anything yet, so
  // there's no reason to replace the case's uploaded report_template_url
  // with the editor's round-tripped copy. Doing so on a brand-new case
  // would strip header/footer/media that the editor's serializer can't
  // perfectly re-emit, and the next refresh would load that degraded blob.
  const hasSeedSavedRef = useRef(false);
  useEffect(() => {
    if (hasSeedSavedRef.current) return;
    if (!hasInitializedRef.current) return;
    if (!activeTemplate || !activeCase) return;
    if (draftStatus?.has_draft) {
      hasSeedSavedRef.current = true;
      return;
    }
    hasSeedSavedRef.current = true;
    void runSave({ uploadDocx: false });
  }, [activeTemplate, activeCase, draftStatus, moduleContent, runSave]);

  const handleDownload = useCallback(async () => {
    setIsDownloading(true);
    try {
      // Make sure the user's most recent edits are persisted (and merged
      // with the case template) on the server, so the .docx we fetch
      // reflects the current editor state, not the previous autosave.
      // handleSave surfaces its own error toast; if it fails we still try
      // the download — the server may have an older draft we can return.
      try {
        await runSave();
      } catch {
        // swallowed — runSave's own error handling already notified the user
      }
      const draftResponseId = draftStatus?.draft_id as string | undefined;
      if (!draftResponseId) {
        dispatch(
          setMessage({
            type: "error",
            content: "Save the draft before downloading.",
          })
        );
        return;
      }
      const stem = caseData?.examinee_name?.replace(/\s+/g, "-") ?? activeCase;
      await downloadDocxMutateAsync({
        responseId: draftResponseId,
        fileName: `${stem}-${template?.name ?? "report"}.docx`,
      });
      void completeResponse;
    } catch (error) {
      console.error("Failed to download .docx:", error);
      dispatch(setMessage({ type: "error", content: "Failed to download .docx." }));
    } finally {
      setIsDownloading(false);
    }
  }, [
    runSave,
    draftStatus?.draft_id,
    downloadDocxMutateAsync,
    caseData?.examinee_name,
    activeCase,
    template?.name,
    completeResponse,
    dispatch,
  ]);

  const isLoading =
    !!activeTemplate && (isTemplateLoading || isDraftLoading || isBufferLoading);

  // Derive the QtA source data straight from the loaded template + draft.
  // We don't route this through `moduleContent` because `moduleContent`
  // only carries the user-editable `text` field, but QtA needs both the
  // raw LOI questions (read-only, populated server-side) AND the saved
  // answers, kept separate so the editor's QtA insert can re-stitch them.
  //
  // Lookup is dual-path because of a backend data-keying issue: the
  // background `auto_map_loi_questions_for_case` task creates
  // response_modules via `get_or_create_response_module` without
  // setting `rm.name`, while the frontend's autosave path creates a
  // *separate* response_module with `name = template_module.name`.
  // The draft-status endpoint then keys `draft_data` by `rm.name` —
  // so the named (and empty) autosave row shadows the nameless (and
  // populated) auto-map row. The direct-name lookup misses the data;
  // the fallback scan finds it by content shape. The underlying backend
  // bug is tracked separately — until it's fixed, this scan keeps the
  // QtA insertion working.
  const qtaSource = useMemo<ContextValue["qtaSource"]>(
    () => deriveQtaSource(orderedModules, draftStatus as QtaDraftStatus),
    [orderedModules, draftStatus]
  );

  // Derive the List-of-Documents source data (MDRS-98). Like `qtaSource`,
  // this lives outside `moduleContent` because the doc-sorter summary is
  // read-only data the user can't author through the modules panel —
  // the editor's documents-table insert helper renders it as a 3-column
  // table directly under the SUMMARY OF DOCUMENTS PROVIDED heading.
  const documentsSource = useMemo<ContextValue["documentsSource"]>(() => {
    const docs = orderedModules.find(({ module }) =>
      LIST_OF_DOCUMENTS_HEADING_TITLE.includes(module.name)
    );
    const draftData = (draftStatus?.draft_data ?? {}) as Record<string, ModuleDraft>;
    const draft = docs ? draftData[docs.module.name] : undefined;
    let summaryText = (draft?.summaries_from_doc_sorter ?? "").trim();
    const expertsResponse = (draft?.experts_response ?? "").trim();
    // MDRS-98: templates without a "List of Documents" module still get the
    // documents schedule — the backend derives it from the case's files and
    // returns it as a top-level `documents_summary`. The docs section is always
    // rendered (see ReportSectionEditor), so fall back to that here.
    if (!summaryText) {
      summaryText = (
        (draftStatus as { documents_summary?: string | null } | undefined)
          ?.documents_summary ?? ""
      ).trim();
    }
    if (!summaryText && !expertsResponse) return null;
    return {
      moduleName: docs?.module.name ?? "SUMMARY OF DOCUMENTS PROVIDED",
      summaryText,
      expertsResponse,
    };
  }, [orderedModules, draftStatus]);

  // ── Template outline hints ────────────────────────────────────────────
  // Fetch the backend's pre-computed structural outline for the case's
  // uploaded template. The first request triggers extraction +
  // persistence server-side (see `services/template_outline.py`);
  // subsequent requests hit the cache.
  const { data: outline } = useTemplateOutline({
    case_number: activeCase ?? "",
    enabled: !!activeCase,
  });
  const outlineHints = useMemo<ContextValue["outlineHints"]>(() => {
    if (!outline) return null;
    return buildOutlineHints(outline);
  }, [outline]);
  const outlineAnchors = useMemo<ContextValue["outlineAnchors"]>(
    () => ({
      cocAnchor: outline?.cocAnchor?.text ?? null,
      qtaAnchor: outline?.qtaAnchor?.text ?? null,
    }),
    [outline]
  );

  const value: ContextValue = {
    isActive: !!activeCase && !!activeTemplate,
    activeCase,
    activeTemplate,
    activate,
    editorRef,
    documentBuffer,
    isBufferLoading,
    bufferError,
    onEditorChange,
    isEditorEmpty,
    isLoading,
    lastSavedAt,
    draftId: (draftStatus?.draft_id as string | null) ?? null,
    template,
    caseData,
    orderedModules,
    moduleContent,
    choiceByModule,
    qtaSource,
    documentsSource,
    outlineHints,
    outlineAnchors,
    activeSection,
    setActiveSection,
    handleSelectSection,
    showAll,
    setShowAll,
    sectionRows,
    activeRow,
    activeModule,
    activeChoices,
    activeNotes,
    allNotes,
    handleChoiceChange,
    handleSave,
    handleDownload,
    isSaving,
    isDownloading,
  };

  return (
    <ReportBuilderEditorContext.Provider value={value}>
      {children}
    </ReportBuilderEditorContext.Provider>
  );
};
